import type {
  Experiment,
  GenericExperiment,
  OverrideExperiment,
  SupportedExperiment,
} from '../types/experiment.types';

/**
 * Takes the list of supported experiments for the application and transforms them into an array of objects in the form:
 * {
 *   experimentKey: string,
 *   variationKey: string, // optional
 *   availableVariationKeys: [string],
 *   shouldFireImpression: bool,
 * }
 * If a given supported experiment has its experimentKey in assignedExperiments (meaning the user is a participant in the experiment), then shouldFireImpression will be set to true and
 * the variationKey will be set to the assigned variation as determined by the experimentation platform.
 *
 * Otherwise, shouldFireImpression will be set to false and the variationKey will not be set.
 *
 * @param {*} supportedExperiments an array of supportedExperiments for the application
 * @param {*} assignedExperiments an array of experiments in form { experimentKey, variationKey } as determined by users personalization context.
 */
export const buildAvailableExperiments = ({
  supportedExperiments = [],
  assignedExperiments = [],
}: {
  supportedExperiments: SupportedExperiment[];
  assignedExperiments: GenericExperiment[];
}): Experiment[] => {
  return supportedExperiments.map((supportedExperiment) => {
    const experiment = assignedExperiments.find(
      (e) => e?.['experimentKey'] === supportedExperiment.experimentKey
    );
    if (
      experiment?.['experimentKey'] &&
      experiment?.['variationKey'] &&
      supportedExperiment.availableVariationKeys.includes(
        experiment['variationKey']
      )
    ) {
      // experiment is supported for site and user is in a valid test cell.
      return {
        experimentKey: experiment['experimentKey'],
        variationKey: experiment['variationKey'],
        availableVariationKeys: supportedExperiment.availableVariationKeys,
        shouldFireImpression: true,
      };
    }
    return {
      experimentKey: supportedExperiment.experimentKey,
      availableVariationKeys: supportedExperiment.availableVariationKeys,
      shouldFireImpression: false,
    };
  });
};

/**
 * Returns a final array of experiments that a given user should be exposed too.
 *
 * Any experiment included in the debugExperimentOverrides parameter takes priority over the
 * same experiment's settings as defined by the availableExperiments.
 *
 * Naive approach for now - filter out all availableExperiments that are present in the override array,
 * then spread the non-overridden experiments together with the overridden experiments from the debugExperimentOverrides parameter.
 *
 * @param {*} availableExperiments an array of experiments available for the application.
 * @param {*} debugExperimentOverrides an array of experiments which can override any given available experiment.
 */
export const buildActiveExperiments = ({
  availableExperiments = [],
  debugExperimentOverrides = [],
}: {
  availableExperiments: Experiment[];
  debugExperimentOverrides: OverrideExperiment[];
}): Experiment[] => {
  const availableExperimentsWithoutOverrides = availableExperiments.filter(
    (availableExperiment) =>
      !debugExperimentOverrides.some(
        (experimentOverride) =>
          experimentOverride.experimentKey === availableExperiment.experimentKey
      )
  );

  return [...availableExperimentsWithoutOverrides, ...debugExperimentOverrides];
};

/**
 * A feature gate is enabled if these requirements are met:
 * 1. It's supported by the fragment (supportedFeatureGates)
 * 2. It's enabled for the user in UBIK user context (ubikFeatureGates)
 */
export const getActiveFeatureGates = ({
  ubikFeatureGates,
  supportedFeatureGates = [],
  debugFeatureGateOverrides = [],
}: {
  supportedFeatureGates: string[];
  ubikFeatureGates: { [key: string]: string | undefined };
  debugFeatureGateOverrides: string[];
}) => {
  return supportedFeatureGates.reduce<string[]>(
    (acc: string[], gate: string) => {
      const isEnabled = ubikFeatureGates?.[gate] === 'true' || false;

      const isOverridden = debugFeatureGateOverrides.includes(gate);

      // if inherently on
      if (isEnabled && !isOverridden) {
        acc.push(gate);
        // if overridden
      } else if (!isEnabled && isOverridden) {
        acc.push(gate);
      }
      return acc;
    },
    []
  );
};
